// Demo guide: "Start here", then one tour per ICT procedure document
// (data/demoTours.ts), driving the real app over real demo records. Each step
// puts the app in the right state (opens the task, email or page), then
// spotlights the part of the screen the step is about. Nothing is faked:
// approving in a tour really approves.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProxyData } from '../contexts/ProxyDataContext';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import { proxyNowStamp, PROXY_JOBS } from '../data/mockProxyJobs';
import { useEmailInbox } from '../contexts/EmailInboxContext';
import { buildProxyIntakeTasks, buildMaterialFollowupTask, materialFollowupTaskFor } from '../data/proxyWorkflowRegistry';
import { DEMO_TOURS, GLOSSARY, type TourStep, type TourCase } from '../data/demoTours';
import { QuickCheck } from './feedback/QuickCheck';

const STORAGE_KEY = 'demo-guide-state';

// `pos` is the panel's top-left once dragged; null means auto-placed.
// `done` lists the tours someone has reached the end of.
type GuideView = 'tour' | 'glossary';
interface GuideState { open: boolean; view: GuideView; tourId: string; stepIdx: number; spotlight: boolean; pos: { x: number; y: number } | null; done: string[] }

function loadState(): GuideState {
  const fallback: GuideState = { open: false, view: 'tour', tourId: DEMO_TOURS[0].id, stepIdx: 0, spotlight: true, pos: null, done: [] };
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    return saved && DEMO_TOURS.some(t => t.id === saved.tourId) && saved.view !== 'contents' ? { ...fallback, ...saved } : fallback;
  } catch {
    return fallback;
  }
}

const ALREADY_DONE = 'You\'ve already approved this one, so it\'s no longer in the queue. Restart the tour to put it back.';

const GRAD = 'linear-gradient(135deg,#14b8a6,#0891b2)';

export function DemoGuide() {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobs, updateJob, resetJobs } = useProxyData();
  const { restoreThreads } = useEmailInbox();
  const ctx = useProxyWorkflowContext();
  const [state, setState] = useState<GuideState>(loadState);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [targetMissing, setTargetMissing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const tour = DEMO_TOURS.find(t => t.id === state.tourId) ?? DEMO_TOURS[0];
  const stepIdx = Math.min(state.stepIdx, tour.steps.length - 1);
  const step = tour.steps[stepIdx];

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* private mode, guide still works */ }
  }, [state]);

  // Put the app in the state a step needs, then go to its screen.
  function runStep(s: TourStep) {
    setNotice(null);
    // Only the bell step leaves the notification panel open.
    window.dispatchEvent(new Event(s.setup?.kind === 'openNotifications' ? 'ace:open-notifications' : 'ace:close-notifications'));
    if (s.setup?.kind === 'openNotifications') return;
    if (s.setup?.kind === 'openTask') {
      const { taskId } = s.setup;
      const task = buildProxyIntakeTasks(ctx).find(t => t.id === taskId);
      if (task) navigate('/ace', { state: { proxyTask: task } });
      else setNotice(ALREADY_DONE);
      return;
    }
    if (s.setup?.kind === 'requestFollowUp') {
      const { jobId } = s.setup;
      const job = jobs.find(j => j.jobId === jobId);
      if (!job) return;
      // Ace already prepared it from the day's follow-up report.
      const existing = materialFollowupTaskFor(buildProxyIntakeTasks(ctx), jobId);
      if (existing) { navigate('/ace', { state: { proxyTask: existing } }); return; }
      const stamp = proxyNowStamp();
      updateJob(jobId, { materialFollowupRequestedAt: stamp });
      navigate('/ace', { state: { proxyTask: buildMaterialFollowupTask(job, stamp) } });
      return;
    }
    if (s.route && `${location.pathname}${location.search}` !== s.route) navigate(s.route);
  }

  // A procedure's other case: open it live, staying on the same step.
  function openCase(c: TourCase) {
    runStep({ title: c.label, withAce: c.note, route: c.route, setup: c.setup });
  }

  // Restart: put every job and email the tour touches back to its starting
  // state, so tasks approved last time are open again, then go to step 1.
  function restartTour() {
    const steps = tour.steps.flatMap(st => [st, ...(st.cases ?? [])]);
    const seedTasks = buildProxyIntakeTasks({ ...ctx, jobs: PROXY_JOBS });
    const jobIds = new Set<string>();
    const emailIds = new Set<string>();
    for (const st of steps) {
      if (st.setup?.kind === 'requestFollowUp') {
        jobIds.add(st.setup.jobId);
        (materialFollowupTaskFor(seedTasks, st.setup.jobId)?.jobIds ?? []).forEach(id => jobIds.add(id));
      }
      if (st.setup?.kind === 'openTask') {
        const { taskId } = st.setup;
        const task = seedTasks.find(t => t.id === taskId);
        (task?.jobIds ?? (task ? [task.jobId] : [])).forEach(id => jobIds.add(id));
      }
      const emailId = st.route?.match(/emailId=([^&]+)/)?.[1];
      if (emailId) emailIds.add(emailId);
    }
    resetJobs([...jobIds]);
    restoreThreads([...emailIds]);
    setState(prev => ({ ...prev, done: prev.done.filter(id => id !== tour.id) }));
    // Let the reset land before step 1 looks its task up.
    window.setTimeout(() => goTo(tour.id, 0), 0);
  }

  function goTo(tourId: string, idx: number) {
    const next = DEMO_TOURS.find(t => t.id === tourId) ?? DEMO_TOURS[0];
    const clamped = Math.max(0, Math.min(idx, next.steps.length - 1));
    const finished = clamped === next.steps.length - 1;
    setState(prev => ({
      ...prev, tourId: next.id, stepIdx: clamped, open: true, view: 'tour',
      done: finished && !prev.done.includes(next.id) ? [...prev.done, next.id] : prev.done,
    }));
    runStep(next.steps[clamped]);
  }

  // Find the step's target and follow it (it may render late, move or scroll).
  // A list of targets means "the first one on screen" (a fallback).
  const targetList = (Array.isArray(step.target) ? step.target : step.target ? [step.target] : []).join('\n');
  useEffect(() => {
    setRect(null);
    setTargetMissing(false);
    const targets = targetList ? targetList.split('\n') : [];
    if (!state.open || state.view !== 'tour' || !targets.length) return;
    let scrolled = false;
    const started = Date.now();
    const iv = window.setInterval(() => {
      const el = targets.map(sel => document.querySelector(sel) as HTMLElement | null).find(Boolean) ?? null;
      const r = el?.getBoundingClientRect();
      if (el && r && r.width > 0 && r.height > 0) {
        if (!scrolled) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); scrolled = true; }
        setRect(prev => (prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height ? prev : r));
        setTargetMissing(false);
      } else {
        setRect(null);
        if (Date.now() - started > 4000) setTargetMissing(true);
      }
    }, 200);
    return () => window.clearInterval(iv);
  }, [state.open, state.view, targetList, location.pathname, location.search, tour.id, stepIdx]);

  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setState(prev => ({ ...prev, open: false })); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.open]);

  // Keep the panel clear of what it's pointing at, until it's been dragged.
  const panelOnLeft = useMemo(() => !!rect && rect.left + rect.width / 2 > window.innerWidth / 2, [rect]);

  // Drag by the header. Clamped so the header always stays on screen.
  function startDrag(e: React.PointerEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).closest('button') || !panelRef.current) return;
    e.preventDefault();
    const box = panelRef.current.getBoundingClientRect();
    const dx = e.clientX - box.left;
    const dy = e.clientY - box.top;
    const onMove = (ev: PointerEvent) => {
      const x = Math.min(Math.max(ev.clientX - dx, 0), window.innerWidth - box.width);
      const y = Math.min(Math.max(ev.clientY - dy, 0), window.innerHeight - 56);
      setState(prev => ({ ...prev, pos: { x, y } }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  if (!state.open) {
    return (
      <button
        onClick={() => goTo(tour.id, stepIdx)}
        title="Open the demo guide"
        className="fixed bottom-5 right-5 z-[460] px-3.5 py-2 rounded-full shadow-lg flex items-center gap-1.5 text-white text-xs font-semibold hover:scale-105 transition-transform"
        style={{ background: GRAD }}
      >
        Demo guide
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M5 2l3 3-3 3" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
    );
  }

  const isLast = stepIdx === tour.steps.length - 1;
  const showView = (view: GuideView) => setState(prev => ({ ...prev, view }));
  const nextTour = DEMO_TOURS[DEMO_TOURS.indexOf(tour) + 1];

  return (
    <>
      {state.view === 'tour' && state.spotlight && rect && (
        <div
          aria-hidden
          className="fixed z-[450] pointer-events-none rounded-xl transition-all duration-200"
          style={{
            top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
            boxShadow: '0 0 0 9999px rgba(4,50,67,.45), 0 0 0 2px #14b8a6',
          }}
        />
      )}

      <div
        ref={panelRef}
        className="fixed z-[460] w-[380px] max-h-[82vh] flex flex-col rounded-2xl bg-white overflow-hidden"
        style={{
          ...(state.pos
            ? { left: Math.max(0, Math.min(state.pos.x, window.innerWidth - 380)), top: Math.max(0, Math.min(state.pos.y, window.innerHeight - 56)) }
            : { bottom: 20, [panelOnLeft ? 'left' : 'right']: 20 }),
          border: '1px solid #E5E7EB', boxShadow: '0 8px 32px rgba(4,50,67,.25)',
        }}
      >
        <div
          onPointerDown={startDrag}
          onDoubleClick={() => setState(prev => ({ ...prev, pos: null }))}
          title="Drag to move · double-click to reset"
          className="flex items-center justify-between px-4 py-3 flex-shrink-0 cursor-move select-none touch-none"
          style={{ background: GRAD }}
        >
          <div>
            <div className="text-xs font-bold text-white uppercase tracking-wider">Demo guide</div>
            <div className="text-xs text-white/60">How Ace handles Proxy work, step by step</div>
          </div>
          <button onClick={() => setState(prev => ({ ...prev, open: false }))} title="Close (Esc)"
            className="w-7 h-7 rounded-md flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
          </button>
        </div>

        {/* One line: which tour you're on (opens the list), and the Glossary. */}
        <div className="relative px-4 py-2 flex items-center gap-2 flex-shrink-0 border-b border-trim">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="flex items-center gap-1.5 text-sm font-semibold text-ink hover:text-teal-700"
            aria-expanded={menuOpen}
          >
            {state.view === 'glossary' ? 'Glossary' : tour.label}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={`transition-transform ${menuOpen ? 'rotate-180' : ''}`}><path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          {state.view === 'tour' && <span className="text-xs text-muted">{DEMO_TOURS.indexOf(tour) + 1} of {DEMO_TOURS.length}</span>}
          <button
            onClick={() => { setMenuOpen(false); showView(state.view === 'glossary' ? 'tour' : 'glossary'); }}
            className="ml-auto text-xs font-medium text-teal-600 hover:underline"
          >
            {state.view === 'glossary' ? 'Back to tour' : 'Glossary'}
          </button>

          {menuOpen && (
            <ul className="absolute left-3 right-3 top-full mt-1 z-10 py-1 rounded-xl bg-white border border-trim shadow-lg max-h-[50vh] overflow-y-auto">
              {DEMO_TOURS.map((t, i) => {
                const active = t.id === tour.id && state.view === 'tour';
                return (
                  <li key={t.id}>
                    <button
                      onClick={() => { setMenuOpen(false); goTo(t.id, 0); }}
                      className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-xs ${active ? 'bg-wash font-semibold text-ink' : 'text-copy hover:bg-wash'}`}
                    >
                      <span className="w-4 text-right text-muted">{i + 1}.</span>
                      <span className="flex-1">{t.label}</span>
                      <span className="text-[11px] text-muted">{t.title}</span>
                      <span className="w-3 text-teal-600">{state.done.includes(t.id) ? '✓' : ''}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {state.view === 'glossary' && (
          <div className="px-4 py-3 overflow-y-auto flex-1 min-h-0">
            <dl className="flex flex-col gap-2.5">
              {GLOSSARY.map(g => (
                <div key={g.term}>
                  <dt className="text-xs font-semibold text-ink">{g.term}</dt>
                  <dd className="text-xs text-copy leading-relaxed mt-0.5">{g.meaning}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {state.view === 'tour' && (
        <div className="px-4 py-3 overflow-y-auto flex-1 min-h-0">
          {stepIdx === 0 && <div className="text-xs text-copy leading-relaxed mb-3 pb-3 border-b border-trim">{tour.summary}</div>}
          <div className="text-xs text-muted">{tour.title} · step {stepIdx + 1} of {tour.steps.length}</div>
          <div className="text-sm font-semibold text-ink mt-0.5">{step.title}</div>

          {step.today && (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">Today</div>
              <div className="text-xs text-copy mt-0.5 leading-relaxed">{step.today}</div>
            </div>
          )}
          <div className="mt-3 px-3 py-2.5 rounded-lg" style={{ background: '#E9F6F6' }}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#0F6E56]">With Ace</div>
            <div className="text-xs text-ink mt-0.5 leading-relaxed">{step.withAce}</div>
          </div>
          {step.cases && (
            <ul className="mt-3 flex flex-col gap-1.5">
              {step.cases.map(c => (
                <li key={c.label} className="flex items-start gap-2 px-3 py-2 rounded-lg border border-trim">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-ink">{c.label}</div>
                    <div className="text-xs text-copy mt-0.5 leading-relaxed">{c.note}</div>
                  </div>
                  <button onClick={() => openCase(c)} className="text-xs font-medium text-teal-600 hover:underline whitespace-nowrap">Open</button>
                </li>
              ))}
            </ul>
          )}
          {notice && (
            <div className="mt-2 flex items-start gap-2 text-xs text-[#92400E]">
              <span className="flex-1">{notice}</span>
              {notice === ALREADY_DONE && (
                <button onClick={restartTour} className="font-semibold text-teal-600 hover:underline whitespace-nowrap">Restart tour</button>
              )}
            </div>
          )}
          {!notice && targetMissing && (
            <div className="mt-2 text-xs text-muted">Nothing to point at on this screen yet. Follow the step above and the spotlight will find it.</div>
          )}
          {isLast && (
            <div className="mt-3 px-3 py-2.5 rounded-lg border border-trim">
              <QuickCheck
                key={tour.id}
                question={`Does this match how your team handles ${tour.label}?`}
                choices={[{ label: 'Yes', good: true }, { label: 'Mostly' }, { label: 'No' }]}
                prompt="What's different for your team?"
                page="Demo guide"
                about={`${tour.label} tour`}
                source="tour-check"
              />
            </div>
          )}

          <ol className="mt-3 flex flex-col gap-0.5">
            {tour.steps.map((s, i) => (
              <li key={s.title}>
                <button
                  onClick={() => goTo(tour.id, i)}
                  className={`w-full text-left flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-xs ${i === stepIdx ? 'bg-wash text-ink font-semibold' : 'text-copy hover:bg-wash'}`}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${i === stepIdx ? 'bg-teal-500 text-white' : 'bg-[#F3F4F6] text-muted'}`}>{i + 1}</span>
                  {s.title}
                </button>
              </li>
            ))}
          </ol>
        </div>
        )}

        {state.view === 'tour' && (
        <div className="px-4 py-3 border-t border-trim flex items-center gap-2 flex-shrink-0">
          <button onClick={() => goTo(tour.id, stepIdx - 1)} disabled={stepIdx === 0}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-trim text-copy disabled:opacity-40">
            Back
          </button>
          <button
            onClick={() => (!isLast ? goTo(tour.id, stepIdx + 1) : goTo((nextTour ?? DEMO_TOURS[0]).id, 0))}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: GRAD }}
            title={isLast && nextTour ? `Next: ${nextTour.label}` : undefined}
          >
            {!isLast ? 'Next' : nextTour ? 'Next procedure' : 'Back to start'}
          </button>
          <label className="ml-auto flex items-center gap-1.5 text-xs text-copy cursor-pointer select-none">
            <input type="checkbox" checked={state.spotlight} onChange={e => setState(prev => ({ ...prev, spotlight: e.target.checked }))} className="accent-teal-500" />
            Spotlight
          </label>
          <button onClick={restartTour} title="Start this tour again with its records reset" className="text-xs font-medium text-teal-600 hover:underline">Restart</button>
        </div>
        )}
      </div>
    </>
  );
}
